import { Navigate, Outlet, useLocation } from "react-router";

import { LoadFailed, Loading } from "@/components/states";
import { AppShell } from "@/components/app-shell";

import { pathForStep, STEP_PATHS, useSession } from "./session";

/**
 * Pages for signed-in people. Anyone not signed in goes to sign-in; anyone
 * with a step to finish (a code, a password, codes to set up, an authority
 * to choose) goes to that step's page, as the server says.
 */
export function SessionGate() {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) {
    return <Loading />;
  }
  if (session.isError) {
    return (
      <LoadFailed
        error={session.error}
        onRetry={() => {
          void session.refetch();
        }}
      />
    );
  }
  if (!session.data) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  const stepPath = pathForStep(session.data.nextStep);
  if (session.data.nextStep !== "NONE" && location.pathname !== stepPath) {
    return <Navigate to={stepPath} replace />;
  }
  return <AppShell session={session.data} />;
}

/**
 * The step pages themselves: reachable only while that step is due (or,
 * for the password, whenever someone is signed in).
 */
export function StepGate({ path }: { path: string }) {
  const session = useSession();
  if (session.isPending) {
    return <Loading />;
  }
  if (!session.data) {
    return <Navigate to="/sign-in" replace />;
  }
  const due = STEP_PATHS[session.data.nextStep];
  if (due !== path && !(path === STEP_PATHS.CHANGE_PASSWORD && session.data.nextStep === "NONE")) {
    return <Navigate to={due} replace />;
  }
  return <Outlet />;
}
